import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';

const DEFAULT_VALUES = [
  { key: 'Acknowledgment', icon: 'fa-handshake', text: 'Acknowledgment: Identify the unique talents of each student and celebrate their individuality.' },
  { key: 'Support', icon: 'fa-hands-holding', text: 'Support: Empower the students to become confident thinkers and lifelong learners.' },
  { key: 'Embrace', icon: 'fa-people-group', text: 'Embrace: Cultivate a culture that promotes cognitive, emotional, physical, and social development.' },
  { key: 'Assist', icon: 'fa-user-check', text: 'Assist: Equip the students with the skills required to face the real world with confidence.' },
  { key: 'Coach', icon: 'fa-chalkboard-user', text: 'Coach: Develop the students\u2019 interpersonal and intrapersonal skills for holistic growth.' },
  { key: 'Deliver', icon: 'fa-bullseye', text: 'Deliver: Help the students identify the best opportunities and develop their passion.' },
  { key: 'Partner', icon: 'fa-people-arrows', text: 'Partner: Team with the parents and community to foster an environment that will help the students fulfill their aspirations.' },
];

const DEFAULT_MISSION_POINTS = [
  'To develop scholastic and co-scholastic personalities and impart knowledge to maximize skills and competencies',
  'To help the students become resourceful thinkers who can solve complex problems through reflection, risk-taking, and critical evaluation',
  'To impart the wisdom that transforms mere knowledge into a meaningful initiative',
  'To ensure sensitivity and commitment to the environment and all beings to save the planet for future generations',
];

const splitLines = (text) => String(text || '').split('\n').map((p) => p.trim()).filter(Boolean);

const CoreValues = () => {
  const [sectionData, setSectionData] = useState({});

  useEffect(() => {
    API.get('/home-sections').then(res => {
      if (res.data?.data) setSectionData(res.data.data);
    }).catch(() => {});
  }, []);

  const sec = sectionData.page_core_values || {};

  const values = sec.items && sec.items.length ? sec.items : DEFAULT_VALUES;

  const [activeValue, setActiveValue] = useState(values[0]?.key || 'Acknowledgment');

  const active = values.find(v => v.key === activeValue) || values[0];

  const kicker = sec.kicker || 'Core Values';
  const heading = sec.heading || 'Core Values';
  const quote = sec.quote || 'We aim to create lifelong learners that engage in a meaningful learning experience which builds the understanding of responsibility towards the community.';
  const missionHeading = sec.mission_heading || 'Our Mission';
  const missionIntro = sec.mission_intro || 'Our mission is to offer a learning environment that is caring, motivating, and supportive. St. Andrews World School, Indirapuram enables students to reach their potential and continually work towards excellence. We aim to be the best education provider globally. In order to achieve this dream, we are continuously working on:';
  const missionPoints = sec.mission_points ? splitLines(sec.mission_points) : DEFAULT_MISSION_POINTS;
  const visionHeading = sec.vision_heading || 'Our Vision';
  const visionPara1 = sec.vision_para1 || 'We persevere towards being the best provider of quality education. In this pursuit, we always strive to be better, deliver results, and serve the community by providing quality education. We aim to develop the student\u2019s awareness of intellectual, artistic, and recreational domains that will assist them to reach the fullest of their potential. We are creating leaders of tomorrow by delivering a curriculum that is a blend of modern academics and traditional values.';
  const visionPara2 = sec.vision_para2 || 'We are determined to create an atmosphere where values are considered an achievement. St. Andrews World School best school gurgoan envisions to be an education provider of excellence and impart values, knowledge and character to the students. We are working towards empowering every student to achieve their dreams. Every student\u2019s dreams and aspirations are supported and at St Andrews World School and the staff is always present to offer the needed support and encouragement.';
  const aboutQuote = sec.about_quote || 'We aim to create lifelong learners that engage in a meaningful learning experience which builds the understanding of responsibility towards the community.';

  return (
    <>
      <SEO
        title="Core Values"
        description="Core Values of St. Andrews World School, Indirapuram."
        keywords="core values, SAWS Indirapuram, St Andrews World School, Indirapuram"
      />

      {/* HERO */}
      <section className="page-hero">
        <img
            src={`${process.env.PUBLIC_URL}/images/page-banners/why-choose-us.jpg`}
            alt="Core Values"
            className="page-hero-img"
            loading="eager"
          />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner">
              <div className="w-full max-w-[900px]">

                <Reveal delayMs={70} className="mt-4">
                  <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-center md:text-left">
                    {heading}
                  </h1>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section bg-white">
        <div className="container">
          {/* Tabs (top) */}
          <Reveal>
            <div className="card p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <i className="fas fa-quote-left" aria-hidden />
                </div>
                <p className="text-body leading-relaxed italic">
                  “{quote}”
                </p>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {values.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setActiveValue(v.key)}
                    className={[
                      'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-all',
                      activeValue === v.key ? 'border-secondary bg-secondary text-white' : 'border-gray-200 bg-white text-primary hover:border-secondary/60 hover:bg-light',
                    ].join(' ')}
                    aria-pressed={activeValue === v.key}
                  >
                    <i className={`fas ${v.icon} text-[13px]`} aria-hidden />
                    {v.key}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Active Content (bottom, full width) */}
          <div className="mt-8">
            <Reveal>
              <div className="card p-7 md:p-9">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-secondary/15 text-secondary flex items-center justify-center shrink-0">
                    <i className={`fas ${active.icon}`} aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2 className="h-section text-2xl md:text-3xl font-bold">{active.key}</h2>
                    <div className="divider-line mt-4" />
                    <p className="text-body leading-relaxed mt-5">{active.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="mt-8 grid grid-cols-1 gap-6">
              <Reveal>
                <div className="card p-7 md:p-9">
                  <h3 className="text-primary text-xl font-bold mb-4">{missionHeading}</h3>
                  <p className="text-body leading-relaxed mb-4">
                    {missionIntro}
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-body">
                    {missionPoints.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delayMs={90}>
                <div className="card p-7 md:p-9">
                  <h3 className="text-primary text-xl font-bold mb-4">{visionHeading}</h3>
                  <p className="text-body leading-relaxed mb-4">
                    {visionPara1}
                  </p>
                  <p className="text-body leading-relaxed">
                    {visionPara2}
                  </p>
                </div>
              </Reveal>

              <Reveal delayMs={140}>
                <div className="card p-7 md:p-9">
                  <h3 className="text-primary text-xl font-bold mb-3">About School</h3>
                  <p className="text-body leading-relaxed italic">
                    "{aboutQuote}"
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CoreValues;